import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface AccuracyTrendChartProps {
  data: Array<{
    subject_name: string;
    accuracy: string;
    total_time: string;
    correct_answers: string;
    questions_attempted: string;
  }> | null;
}

const AccuracyTrendChart: React.FC<AccuracyTrendChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        <i className="fas fa-bullseye fa-2x mb-2"></i>
        <p>No accuracy data available</p>
      </div>
    );
  }

  const chartData = {
    labels: data.map(item => item.subject_name.length > 10 ? 
      item.subject_name.substring(0, 10) + '...' : 
      item.subject_name
    ),
    datasets: [
      {
        label: 'Subject Accuracy',
        data: data.map(item => parseFloat(item.accuracy)),
        backgroundColor: data.map(item => {
          const accuracy = parseFloat(item.accuracy);
          if (accuracy >= 80) return 'rgba(40, 167, 69, 0.8)';    // Green for high accuracy
          if (accuracy >= 60) return 'rgba(255, 193, 7, 0.8)';    // Yellow for medium accuracy
          if (accuracy >= 40) return 'rgba(23, 162, 184, 0.8)';   // Blue for fair accuracy
          return 'rgba(220, 53, 69, 0.8)';                        // Red for low accuracy
        }),
        borderColor: data.map(item => {
          const accuracy = parseFloat(item.accuracy);
          if (accuracy >= 80) return 'rgba(40, 167, 69, 1)';      // Green border
          if (accuracy >= 60) return 'rgba(255, 193, 7, 1)';      // Yellow border
          if (accuracy >= 40) return 'rgba(23, 162, 184, 1)';     // Blue border
          return 'rgba(220, 53, 69, 1)';                          // Red border
        }),
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(54, 162, 235, 0.8)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context: any) {
            const index = context[0].dataIndex;
            return `Subject: ${data[index].subject_name}`;
          },
          label: function(context: any) {
            const index = context.dataIndex;
            const item = data[index];
            return [
              `Accuracy: ${parseFloat(item.accuracy).toFixed(1)}%`,
              `Correct: ${item.correct_answers}/${item.questions_attempted}`,
              `Time: ${Math.floor(parseInt(item.total_time) / 60)}m ${parseInt(item.total_time) % 60}s`
            ];
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Subjects',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Accuracy (%)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          callback: function(value: any) {
            return value + '%';
          },
        },
      },
    },
  };

  return (
    <div style={{ height: '300px', width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default AccuracyTrendChart;
